// offline-backend.ts — the Backend decorator backlit.offline.enable()
// installs over the remote transport (backend.ts setBackend). Every namespace
// method and JSON helper resolves getBackend() per call, so once installed the
// decorator sits in front of all of them. It reaches the runtime's shared
// state (stores, identity, options, events) only through the OfflineContext
// that offline.ts hands in.
//
// Identity: authMe remembers the signed-in user and, on a transport failure,
// answers the cached identity (D4).
//
// Read path (Phase A) for the mirrored stores (data / userdata / records that
// are in options().stores; capture is never mirrored):
//   - network-first, and an online answer is returned untouched;
//   - write-through: a 2xx is copied into the mirror (not awaited) only when it
//     carries X-Backlit-CRC32C (single-key reads), was not redirected, and its
//     own X-Backlit-UID names the partition's user (the "g" partition takes
//     any 2xx) — so a captive-portal page or another user's answer never lands;
//   - a JSON not_found 404 leaves a tombstone under the same gate;
//   - every fold carries its request's interval (seenFrom = just before the
//     request, seenTo = its response) and the mirror reconciles it against the
//     stored evidence (offline-store.ts reconcile), so a late answer never
//     overwrites newer truth — overlapping answers that disagree leave
//     "unknown";
//   - ONLY a code:"network" failure falls back: a value on the device answers
//     with fromCache; a tombstone or a complete listing (with no entry for the
//     key) answers a fromCache not_found — an "unknown" mirror entry never
//     does; anything else rethrows the original error unchanged;
//   - list / getBatch pages are authoritative for the key range they cover
//     (pruning mirrored keys the server no longer lists, when the entry is
//     strictly older than the page; the cursor → previous-page chain is
//     recorded synchronously as each page returns); offline, a first page
//     is answered from the mirror with the server's clamps and "!" SDK cursors,
//     which are always answered from the mirror (even online). A page answered
//     from the mirror carries complete (online pages never do): true only when
//     a complete listing covers the prefix (or an ancestor), no unknown entry
//     lies in the page's range and every earlier page was complete — the SDK
//     cursor carries that last bit ("!." = incomplete so far), so the final
//     page's flag speaks for the whole listing. The queued-op overlay inserts
//     a key strictly after the cursor's key (the decoded SDK cursor, or the
//     chained page's last key), so a queued key between pages appears once;
//   - readTimeoutMs (opt-in): a single-key read with a page proof and a cached
//     value answers from the mirror once the network has taken that long, and
//     the network answer still writes through when it arrives;
//   - realtime frames the app's own subscription receives only ever invalidate
//     (→ "unknown"): a frame is evidence as of [0, receivedAt], so it never
//     states a value — a set with the stored crc, or an entry whose evidence
//     began after receipt, is left alone; a stale per-user partition (D3) is
//     deleted on read instead of served;
//   - every mirror write (write-through, tombstone, list page, frame, a
//     prefetch fold — handle.fold, offline-prefetch.ts) captures
//     the purge epoch (ctx.epoch(), offline.ts) as its request starts (a
//     frame: on receipt) and re-checks it synchronously right before the store
//     call — a purge in between drops the write (Rule D).
//
// Write path (Phase B, writes:"queue", stores in options().stores):
//   - a put / putIfMatch / delete goes online first and is queued in the
//     outbox (resolving pending:true) when it fails with code:"network" — or,
//     without trying the network, when the key already has ops in the outbox
//     for this uid ("no overtaking"). Queueing needs a signed-in user (data: a
//     non-viewer); a stale identity still queues, quarantined;
//   - the base a queued write replays against comes from the local view (the
//     key's latest queued op, else the mirror): putIfMatch runs as a LOCAL
//     compare-and-swap, a delete of a key known absent throws not_found;
//   - putIfMatch(k, v, "") of a key the device has never seen ("unseen": no
//     entry, no complete listing covering it, a fresh partition read without
//     error) queues an unseen create-only op (base "", unseen:true): it
//     replays through the -cas route with an empty expected crc, so a key
//     taken meanwhile answers a 412 witness and enters the conflict pipeline,
//     and a dropped unseen create restates nothing (no corrective delete).
//     Any other CAS the device can't decide locally — a non-empty expected
//     crc on an unseen key, a crc-less or "unknown" entry, a stale partition
//     or a store error — rethrows network (D-U1);
//   - the mirror is never written for a queued write (CON-6): reads overlay the
//     current uid's queued ops instead (a queued key answers from the outbox
//     without a request; list / getBatch pages get rows replaced, removed and
//     inserted); a local storage.change{pending:true} reaches this page's on()
//     handlers and the same uid's other tabs, and the drain is asked to run
//     (offline-sync.ts);
//   - an acknowledged online write is written through to the mirror, on a
//     per-page serial queue (one body read at a time), as evidence over
//     [request start, response]: a put / matched CAS stores the value
//     (metadata only past maxBytes / 4 — the body is then never read), a 412
//     stores its witness, a delete (204 or 404) or a CAS 404 tombstones. It
//     is gated on the response's own X-Backlit-UID: a foreign uid or a
//     redirect touches nothing; no meta, no uid on a per-user partition, a
//     records answer with no owner, an unreadable body, an ArrayBuffer (view)
//     mutated in flight, or a failed store invalidates (→ "unknown"). Mirror
//     reads of the key — the CAS base, the offline fallback, the
//     readTimeoutMs race, a realtime frame — first await its pending
//     write-through, so the page's own echo never invalidates it. reassign
//     and the capture reads pass straight through.
//
// Capture (opt-in: "capture" in options().stores, writes:"queue", and
// crypto.getRandomValues present — else create / update pass straight
// through, byte-for-byte unchanged): every create mints {prefix}-{uuid v4} on
// the device and stores it with create-at (transport.ts captureCreateAt); an
// older data-api's 404 / 405 falls back to the legacy ?prefix= create (the
// server's handle), and is remembered for the decorator's life. A network
// failure — or, for an update, a queued op of the same handle (no
// overtaking) — queues the write under CAPTURE_UID, resolving pending:true.
// Capture values are never mirrored, never overlaid, fire no storage.change,
// and their ops carry no identity (offline-sync.ts replays them in every pass).
//
// Device-view reads (offline.peek / peekJSON / cached, via handle.device) never
// touch the network. A per-user partition answers only once this page holds a
// page proof or has seen a real transport failure (ctx.transportFailed()) —
// never the previous user's data on a merely slow network; the "g" partition
// is exempt, and a stale one is deleted (offlinePart) and answers nothing.
//   - peek: the uid's latest queued op (a put → pending:true; a delete →
//     null), else — once the key's pending write-through has landed — a
//     present entry's bytes (fromCache:true), else null (metadata-only,
//     unknown, tombstone, covered-absent, never seen, a store failure). It
//     records an LRU access for a key at most once per TOUCH_EVERY_MS;
//   - cached: a metadata page (scanMeta) of { key, crc32c, contentType, size,
//     hasValue } rows (+ owner for records) with "!" SDK cursors and the same
//     complete rule as a mirror page; unknown entries are omitted (and make
//     the page incomplete), the uid's queued ops are overlaid (a put:
//     hasValue + pending; a delete: removed). No value budget, no LRU touch.

import type { Backend, BacklitUser, BlobResult, ListEntry, ListOptions, ListPage, RecordResult, StorageChangeEvent } from "./backend.js";
import { crc32cBytes, formatCRC32C } from "./crc32c.js";
import { BacklitError } from "./errors.js";
import { dispatchLocalChange, observeStorageChanges } from "./events.js";
import { carryTags } from "./json.js";
import { OPID } from "./offline-conflict.js";
import {
  isStale,
  isUserPartition,
  now,
  partitionFor,
  writeThroughAllowed,
  type IdentityRecord,
  type MirrorStoreName,
} from "./offline-identity.js";
import {
  CAPTURE_UID,
  seen,
  type KeyRangeSpec,
  type ListRow,
  type MirrorEntry,
  type MirrorMeta,
  type MirrorStore,
  type NewOutboxOp,
  type OpKind,
  type Outbox,
  type OutboxOp,
  type PutResult,
} from "./offline-store.js";
import { randomHex, type OfflineChannel } from "./offline-channel.js";
import type { DeviceEntry, DevicePage, OfflineEvent, OfflineEventNext, OfflineOptions, OfflineStatus, PendingWrite, RecordDeviceEntry } from "./offline.js";
import { requestDrain } from "./offline-sync.js";
import { captureCreateAt, deleteWithMeta, responseMetaOf, type ResponseMeta } from "./transport.js";
import { validateHandle } from "./validate.js";

// ResolvedOfflineOptions is enable()'s validated options with defaults applied.
export interface ResolvedOfflineOptions {
  stores: ReadonlySet<"data" | "userdata" | "records" | "capture">;
  writes: "queue" | "none";
  onConflict: NonNullable<OfflineOptions["onConflict"]> | null;
  readTimeoutMs: number; // 0 = off
  maxBytes: number;
  probeMs: number; // 0 = off; else ≥ 5 000
  persist: boolean;
}

// OfflineContext is the runtime's shared state, as the decorator (and the
// sync/conflict engines) see it:
//   - mirror / outbox: the two on-device stores;
//   - options(): the current options (a later enable() replaces them);
//   - identity(): the in-memory identity record (null before it loads);
//   - hasPageProof(): this page itself saw the record's uid proven, at the
//     record's current generation;
//   - cachedUser() / rememberUser(): the D4 identity auth.me() answers offline
//     (served only for the record's own uid and while it is not stale);
//   - noteOnline(): feed the status().online flag;
//   - purgeStale(): delete every per-user partition when the record is stale;
//   - epoch(): the purge epoch — every purge advances it synchronously, so a
//     mirror write whose request began at another epoch is dropped;
//   - emit(): deliver an event to watch() callbacks;
//   - channel(): the cross-tab channel, or null where there is none;
//   - setReason(): set status().reason ("" clears it);
//   - status(): offline.status();
//   - emitState(): schedule a `state` event (fire-and-forget, coalescing —
//     never await it from inside a serial task);
//   - writeSettled(): optional — a decorator write (inFlightWrites) settled;
//   - transportFailed(): this page's last data-plane request (or /ui) got no
//     answer at all, and no response or 2xx beat has arrived since (a timeout
//     never sets it);
//   - tabId: this page's id.
export interface OfflineContext {
  readonly mirror: MirrorStore;
  readonly outbox: Outbox;
  options(): ResolvedOfflineOptions;
  identity(): IdentityRecord | null;
  hasPageProof(): boolean;
  cachedUser(): BacklitUser | null;
  rememberUser(u: BacklitUser): void;
  noteOnline(ok: boolean): void;
  purgeStale(): Promise<void>;
  epoch(): number;
  emit(e: OfflineEvent | OfflineEventNext): void;
  channel(): OfflineChannel | null;
  setReason(r: string): void;
  status(): Promise<OfflineStatus>;
  emitState(): void;
  writeSettled?(): void;
  transportFailed(): boolean;
  readonly tabId: string;
}

export interface OfflineBackendHandle {
  backend: Backend;
  // dispose releases whatever the decorator registered (disable()).
  dispose(): void;
  // settle resolves once every in-flight on-device write has finished (tests).
  settle(): Promise<void>;
  // inFlightWrites counts the app's decorator writes (a store write, or an
  // opted-in capture create / update) not yet settled — a network failure
  // queues only once its write settles.
  inFlightWrites(): number;
  // device answers offline.peek / offline.cached from this device alone (never
  // the network, never rejecting); the caller validated the arguments.
  device: {
    peek(s: MirrorStoreName, k: string): Promise<BlobResult | RecordResult | null>;
    cached(s: MirrorStoreName, prefix: string, opts: ListOptions | undefined): Promise<DevicePage<DeviceEntry | RecordDeviceEntry>>;
  };
  // fold folds answers offline.prefetch (offline-prefetch.ts) requested from
  // the remote itself, under the same gate, epoch check and reconcile as the
  // decorator's own reads (the caller keeps the listing's continuity — never
  // the shared chain):
  //   - page: one list (0) / getBatch (1) page continuing `from`; resolves
  //     whether it was folded (a store failure rejects);
  //   - value: a single-key answer — a result, or its not_found 404 (a
  //     tombstone) — resolving the store's outcome, or "skipped" when the gate
  //     or the epoch refused it.
  fold: {
    page(kind: Kind, s: MirrorStoreName, prefix: string, from: Link, page: ListPage<Row>, m: ResponseMeta | undefined, iv: Interval): Promise<boolean>;
    value(s: MirrorStoreName, k: string, r: BlobResult | BacklitError, m: ResponseMeta | undefined, iv: Interval): Promise<PutResult | "skipped">;
  };
}

// Interval is a request's evidence interval [t0, t1] and the purge epoch
// captured as it started.
export type Interval = { t0: number; t1: number; ep: number };

// BATCH_BUDGET mirrors the server's per-response getBatch value budget: an
// offline page stops before it would exceed it (always at least one row).
const BATCH_BUDGET = 10_485_760;
// CHAIN_MAX bounds the server-cursor → previous-page map (oldest dropped).
const CHAIN_MAX = 256;
// TOUCH_EVERY_MS: a peek records an LRU access for a key at most this often
// (D-DV3); TOUCH_MAX bounds the per-key last-touch map (oldest dropped).
const TOUCH_EVERY_MS = 60_000;
const TOUCH_MAX = 1024;

// readCursor decodes an SDK cursor — "!" + base64url(last key) when every
// page so far was complete, "!." + base64url when one was not ("." is not a
// base64url character) — to the key the next page starts after; a malformed
// one throws invalid_cursor.
export function readCursor(c: string): { after: string; complete: boolean } {
  const complete = c[1] !== ".";
  try {
    return { after: atob(c.slice(complete ? 1 : 2).replace(/-/g, "+").replace(/_/g, "/")), complete };
  } catch {
    throw new BacklitError("invalid_cursor", "invalid cursor", 0);
  }
}

// writeCursor is readCursor's inverse.
function writeCursor(last: string, complete: boolean): string {
  return (complete ? "!" : "!.") + btoa(last).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Kind is the page kind: 0 = list (metadata rows), 1 = getBatch (rows also
// carry the value).
export type Kind = 0 | 1;

// Link is the page a server cursor continues: its last key, and whether the
// pages so far started at the prefix's head (and were all folded).
export type Link = { lastKey: string; fromStart: boolean };

// Row is a list / getBatch row as the decorator reads it: batch rows may carry
// the value, records rows the owner.
export type Row = ListEntry & { value?: Blob; owner?: string };

// MIRRORED lists the stores whose reads the decorator overrides.
const MIRRORED = ["data", "userdata", "records"] as const;

// cur is the live decorator's context (emitLocalChange / listPending reach the
// channel and the outbox through it). hint caches which uid\0store\0key have
// ops in the outbox, so a key without any costs no outbox read; null = reload
// on next use. It only ever errs towards "has ops" (a read then finds none).
let cur: OfflineContext | null = null;
let hint: Promise<Set<string>> | null = null;

const hk = (uid: string, s: string, k: string) => uid + "\0" + s + "\0" + k;

// isNet reports a transport failure (the only failure the offline layer answers).
const isNet = (e: unknown): e is BacklitError => e instanceof BacklitError && e.code === "network";

// full is the network failure a full (or unwritable) outbox answers with.
const full = () => new BacklitError("network", "offline write queue is full", 0);

// crcOf is the crc the server computes for bytes (over the uncompressed body).
const crcOf = (b: ArrayBuffer) => formatCRC32C(crc32cBytes(new Uint8Array(b)));

// read reads a (normalized) body into bytes, once.
const read = (b: BodyInit | undefined) => (b instanceof Blob ? b : new Blob([b as BlobPart])).arrayBuffer();

// sizeOf is a (normalized) body's byte length, without reading a Blob.
const sizeOf = (b: BodyInit | undefined): number =>
  b instanceof Blob ? b.size : b instanceof ArrayBuffer || ArrayBuffer.isView(b) ? b.byteLength : new Blob([(b ?? "") as BlobPart]).size;

// mutable: the body's bytes may change after the call (an ArrayBuffer or a view).
const mutable = (b: BodyInit | undefined) => b instanceof ArrayBuffer || ArrayBuffer.isView(b);

// Ack is what an acknowledged online write states about its key: a value
// (the body to read, the server's crc, the content type) or absent (0).
type Ack = { body: BodyInit | undefined; crc: string; ct: string } | 0;

// opName is an outbox op's PendingWrite / event operation.
export const opName = (o: Pick<OutboxOp, "op">): "set" | "delete" | "create" =>
  o.op === "delete" ? "delete" : o.op === "captureCreate" ? "create" : "set";

// uuidV4 is a random RFC 4122 version-4 UUID in lower-hex 8-4-4-4-12: 16
// bytes from crypto.getRandomValues (randomHex), with the version nibble
// (byte 6 high) set to 4 and the variant bits (byte 8 high) to 10 — the same
// as b[6] = (b[6] & 0x0f) | 0x40 and b[8] = (b[8] & 0x3f) | 0x80. Never the
// crypto UUID helper (secure contexts only) and never a non-cryptographic
// PRNG: the handle it becomes is a capability. Throws where there is no
// getRandomValues.
export function uuidV4(): string {
  const h = randomHex();
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-${"89ab"[parseInt(h[16] as string, 16) & 3]}${h.slice(17, 20)}-${h.slice(20)}`;
}

// captureOptIn reports the offline capture opt-in: the decorator is live with
// "capture" in its stores and writes:"queue", and the page has
// crypto.getRandomValues (without it nothing is minted or queued).
export function captureOptIn(): boolean {
  const o = cur?.options();
  return !!o && o.writes === "queue" && o.stores.has("capture") && typeof globalThis.crypto?.getRandomValues === "function";
}

// loadHint loads the hint (from the whole outbox) when there is none.
const loadHint = (c: OfflineContext) =>
  (hint ||= c.outbox.all().then(
    (all) => new Set(all.map((o) => hk(o.uid, o.store, o.key))),
    () => ((hint = null), new Set<string>()),
  ));

// invalidatePendingHint drops the hint (another tab or the drain changed the
// outbox).
export function invalidatePendingHint(): void {
  hint = null;
}

// hasPending resolves whether the hint names any op of uid ("" = nobody) or
// of capture (a drain trigger).
export async function hasPending(uid: string): Promise<boolean> {
  return !!cur && [...(await loadHint(cur))].some((h) => h.startsWith(uid + "\0") || h.startsWith(CAPTURE_UID + "\0"));
}

// emitLocalChange delivers a local storage.change to this page's on()
// handlers and relays it to the other tabs, which deliver it only when they
// are signed in as uid.
export function emitLocalChange(uid: string, ev: StorageChangeEvent): void {
  dispatchLocalChange(ev);
  cur?.channel()?.post({ type: "change", uid, ev });
}

// listPending is offline.pending(): the current uid's (and capture's) queued
// writes in queue order, without their bodies.
export async function listPending(): Promise<PendingWrite[]> {
  try {
    const c = cur as OfflineContext;
    const uid = c.identity()?.uid;
    return (await c.outbox.all())
      .filter((o) => o.uid === uid || o.uid === CAPTURE_UID)
      .map((o) => ({
        store: o.store,
        key: o.key,
        operation: opName(o),
        queuedAt: o.createdAt,
        state: o.state === "inflight" ? "queued" : o.state,
        ...(o.lastError ? { lastError: o.lastError } : {}),
      }));
  } catch {
    return [];
  }
}

// View is a key's local state as a queued write sees it:
// "present" (the crc, the bytes when on the device), "absent" (a
// tombstone, or covered-absent), "presentUnknown" (a crc-less present entry,
// or an "unknown" one), "unseen" (no entry, not covered, a fresh partition
// read without error), "unavailable" (a stale partition, or a store error).
type Present = { c: "present"; crc32c: string; bytes: ArrayBuffer | null; contentType: string; owner?: string | undefined; pending?: true };
type View = Present | { c: "absent" | "presentUnknown" | "unseen" | "unavailable" };

export function createOfflineBackend(remote: Backend, ctx: OfflineContext): OfflineBackendHandle {
  // writes holds every in-flight mirror write (never awaited by the caller).
  const writes = new Set<Promise<void>>();
  // chain maps "s|p|prefix|<server cursor>" to the page that cursor continues
  // (p: the current partition, "" when none).
  const chain = new Map<string, Link>();
  cur = ctx;
  hint = null;
  // calls counts the in-flight decorator writes (inFlightWrites): counted
  // runs one, from entry until it settles.
  let calls = 0;
  const counted = <T>(run: () => Promise<T>): Promise<T> => {
    calls++;
    const out = run();
    const end = () => {
      calls--;
      ctx.writeSettled?.();
    };
    out.then(end, end);
    return out;
  };

  const track = (p: Promise<unknown>): void => {
    const q: Promise<void> = p.then(
      () => void writes.delete(q),
      () => void writes.delete(q),
    );
    writes.add(q);
  };
  // wt is the per-page serial write-through queue (one online write's body
  // read at a time); inflight maps s\0p\0k to the key's latest pending
  // write-through (a newer one lands after it, on the same queue).
  let wt: Promise<void> = Promise.resolve();
  const inflight = new Map<string, Promise<void>>();
  const wk = (s: string, p: string, k: string) => s + "\0" + p + "\0" + k;
  // landed resolves once k's pending write-through in partition p, if any,
  // has landed — every mirror read of a key the page just wrote awaits it.
  const landed = async (s: MirrorStoreName, p: string, k: string): Promise<void> => {
    const q = inflight.get(wk(s, p, k));
    if (q) await q;
  };
  const on = (s: MirrorStoreName) => ctx.options().stores.has(s);
  const part = (s: MirrorStoreName) => partitionFor(s, ctx.identity());
  // stale: a per-user partition whose identity proof is too old (D3).
  const stale = (p: string) => isUserPartition(p) && isStale(ctx.identity() as IdentityRecord, now());
  // offlinePart is the partition an offline answer for s may come from: null
  // when there is none, or — after deleting it — when it is stale.
  const offlinePart = async (s: MirrorStoreName): Promise<string | null> => {
    const p = part(s);
    if (p && stale(p)) {
      await ctx.purgeStale();
      return null;
    }
    return p;
  };
  // gate returns the partition a response may be written into, or null. It is
  // evaluated again synchronously right before each store write, so a write
  // never lands after an identity change or purge it raced with.
  const gate = (s: MirrorStoreName, m: ResponseMeta | undefined): string | null => {
    const p = part(s);
    return p && m && !m.redirected && writeThroughAllowed(p, m.uid) ? p : null;
  };
  const own = (s: MirrorStoreName, o: string | undefined) => (s === "records" ? { owner: o ?? "" } : {});
  // tomb is the tombstone a 404 answer to a request made over [t0, t1] leaves.
  const tomb = (s: MirrorStoreName, p: string, k: string, t0: number, t1: number): MirrorEntry => ({
    s,
    p,
    k,
    state: "absent",
    value: null,
    contentType: "",
    crc32c: "",
    size: 0,
    fetchedAt: t1,
    accessedAt: 0,
    seenFrom: t0,
    seenTo: t1,
  });
  // stored is a single-key answer built from bytes on the device: the value
  // as a Blob of its content type, the crc, (records) the owner, and tags.
  const stored = (s: MirrorStoreName, b: ArrayBuffer, ct: string, crc: string, owner: string | undefined, tags: object) => ({
    value: new Blob([b], { type: ct }),
    contentType: ct,
    crc32c: crc,
    ...own(s, owner),
    ...tags,
  });
  const cachedResult = (s: MirrorStoreName, e: MirrorEntry) => stored(s, e.value as ArrayBuffer, e.contentType, e.crc32c, e.owner, { fromCache: true });
  // row is a list / getBatch row (a getBatch row with no bytes on the device
  // is metadata-only, reason "not_cached").
  const row = (kind: Kind, s: MirrorStoreName, key: string, crc: string, ct: string, size: number, owner: string | undefined, v: ArrayBuffer | null, tags?: object) => {
    const it = { key, crc32c: crc, contentType: ct, size, ...own(s, owner), ...tags };
    return !kind ? it : v ? { ...it, value: new Blob([v], { type: ct }) } : { ...it, omitted: true, reason: "not_cached" };
  };

  // The known-absent answer: the online not_found shape, tagged fromCache.
  const absent = () => Object.defineProperty(new BacklitError("not_found", "not found", 404), "fromCache", { value: true, enumerable: true });

  // peekEntry reads k's on-device state: the present entry (metadata-only
  // included), 0 when it is known absent (a tombstone, or no entry under a
  // complete listing), else null. A store failure reads as null (no on-device
  // answer). touch records the access for LRU.
  async function peekEntry(s: MirrorStoreName, p: string, k: string, touch = true): Promise<MirrorEntry | 0 | null> {
    try {
      const e = await ctx.mirror.get(s, p, k, { touch });
      // An "unknown" entry comes back as itself (value null, crc ""): lookEntry()
      // gives no answer for it, and coverage is never consulted — absence is
      // never inferred for a key the device holds nothing reliable about.
      if (e) return e.state === "absent" ? 0 : e;
      return (await ctx.mirror.isCoveredAbsent(s, p, k)) ? 0 : null;
    } catch {
      return null;
    }
  }

  // lookEntry is peekEntry for an answer: a metadata-only entry is no answer.
  async function lookEntry(s: MirrorStoreName, p: string, k: string): Promise<MirrorEntry | 0 | null> {
    const e = await peekEntry(s, p, k);
    return e && !e.value ? null : e;
  }

  const pendingHint = () => loadHint(ctx);

  // ops returns uid's outbox ops for (s, k), oldest first ([] when the hint
  // rules the key out, or the outbox can't be read).
  async function ops(uid: string, s: OutboxOp["store"], k: string): Promise<OutboxOp[]> {
    try {
      return uid && (await pendingHint()).has(hk(uid, s, k)) ? await ctx.outbox.forKey(uid, s, k) : [];
    } catch {
      return [];
    }
  }

  // settle turns the network answer to a single-key read made at t0 (purge
  // epoch ep) into the caller's answer: write-through on success, the
  // tombstone on a JSON 404 (both as evidence over [t0, response], and only
  // while the epoch is still ep), the mirror on a transport failure.
  async function settle<R extends BlobResult>(s: MirrorStoreName, k: string, t0: number, ep: number, net: Promise<R>): Promise<R> {
    try {
      const r = await net;
      const t1 = now();
      ctx.noteOnline(true);
      const m = responseMetaOf(r);
      if (m && m.crcPresent && gate(s, m)) track(foldValue(s, k, r, m, { t0, t1, ep }));
      return r;
    } catch (e) {
      if (e instanceof BacklitError) {
        if (e.code === "network") {
          ctx.noteOnline(false);
          const p = await offlinePart(s);
          if (p) await landed(s, p, k);
          const hit = p && (await lookEntry(s, p, k));
          if (hit) return cachedResult(s, hit) as R;
          if (hit === 0) throw absent();
        } else if (e.code === "not_found" && e.status === 404) {
          track(foldValue(s, k, e, responseMetaOf(e), { t0, t1: now(), ep }));
        }
      }
      throw e; // the original error, unchanged
    }
  }

  // foldValue writes a single-key answer requested over iv through to the
  // mirror: a result (with X-Backlit-CRC32C) as the present value, a
  // not_found 404 as a tombstone — each only while gate() admits its response
  // and the epoch is still iv.ep, both checked synchronously right before the
  // store call ("skipped" otherwise).
  async function foldValue(s: MirrorStoreName, k: string, r: BlobResult | BacklitError, m: ResponseMeta | undefined, iv: Interval): Promise<PutResult | "skipped"> {
    if (r instanceof BacklitError) {
      const p = r.code === "not_found" && r.status === 404 ? gate(s, m) : null;
      return p && ctx.epoch() === iv.ep ? ctx.mirror.put(tomb(s, p, k, iv.t0, iv.t1)) : "skipped";
    }
    if (!m || !m.crcPresent || !gate(s, m)) return "skipped";
    const v = await r.value.arrayBuffer();
    const p = gate(s, m);
    if (!p || ctx.epoch() !== iv.ep) return "skipped";
    // r is the remote's own result: contentType, crc32c and (records) owner;
    // its Blob is replaced by the bytes.
    return ctx.mirror.put({
      ...r,
      s,
      p,
      k,
      state: "present",
      value: v,
      size: v.byteLength,
      fetchedAt: iv.t1,
      accessedAt: 0,
      seenFrom: iv.t0,
      seenTo: iv.t1,
    });
  }

  // uidNow is the current identity's uid ("" = nobody signed in).
  const uidNow = () => ctx.identity()?.uid ?? "";

  async function get<R extends BlobResult>(s: MirrorStoreName, k: string, call: () => Promise<R>): Promise<R> {
    if (!on(s)) return call();
    const p = part(s);
    const uid = uidNow();
    // A key with a queued op answers from the outbox, without a request (the
    // read-side twin of no-overtaking) — except a stale per-user partition.
    const last = p && stale(p) ? null : (await ops(uid, s, k)).pop();
    if (last) {
      if (!last.bytes) throw new BacklitError("not_found", "no value at this key", 404); // a queued delete
      const e = s === "records" && p ? await peekEntry(s, p, k) : 0;
      return stored(s, last.bytes, last.contentType, last.crc32c, (e && e.owner) || uid, { pending: true }) as unknown as R;
    }
    const t0 = now();
    const out = settle(s, k, t0, ctx.epoch(), call());
    const t = ctx.options().readTimeoutMs;
    if (t > 0 && p && ctx.hasPageProof() && !stale(p)) {
      // Lie-fi: a cached value answers once the network has taken t ms.
      await landed(s, p, k);
      const hit = await lookEntry(s, p, k);
      if (hit) {
        out.catch(() => {});
        const r = await Promise.race([out, new Promise<void>((ok) => setTimeout(ok, t))]);
        return r || (cachedResult(s, hit) as R);
      }
    }
    return out;
  }

  // ck is a server cursor's chain key (the current partition, "" when none).
  const ck = (s: MirrorStoreName, prefix: string, c: string) => s + "|" + (part(s) ?? "") + "|" + prefix + "|" + c;

  // link records — synchronously, as the page returns (R1-9) — the chain
  // entry page.next continues from, and returns the page this one continues:
  // the head of the prefix for a first page, a chained earlier page for a
  // known server cursor, else undefined. The listing stays fromStart only
  // while every page so far was folded into the mirror.
  function link(s: MirrorStoreName, prefix: string, cursor: string, page: ListPage<Row>, folded: boolean): Link | undefined {
    const prev = cursor ? chain.get(ck(s, prefix, cursor)) : { lastKey: "", fromStart: true };
    const last = page.items.length > 0 ? (page.items[page.items.length - 1] as Row).key : "";
    if (page.next !== "" && last !== "") {
      chain.set(ck(s, prefix, page.next), { lastKey: last, fromStart: !!prev && prev.fromStart && folded });
      if (chain.size > CHAIN_MAX) chain.delete(chain.keys().next().value as string);
    }
    return prev;
  }

  // writePage folds one online list / getBatch page, requested over [t0, t1]
  // at purge epoch ep and continuing prev (link), into the mirror; resolves
  // whether it did (the gate and the epoch are re-checked after the reads).
  async function writePage(
    kind: Kind,
    s: MirrorStoreName,
    prefix: string,
    page: ListPage<Row>,
    m: ResponseMeta | undefined,
    prev: Link | undefined,
    t0: number,
    t1: number,
    ep: number,
  ): Promise<boolean> {
    const rows: ListRow[] = [];
    for (const it of page.items) {
      const row: ListRow = { k: it.key, crc32c: it.crc32c, contentType: it.contentType, size: it.size, ...own(s, it.owner) };
      if (kind && it.crc32c !== "" && it.value) row.value = await it.value.arrayBuffer();
      rows.push(row);
    }
    const p = gate(s, m);
    if (!p || ctx.epoch() !== ep) return false;
    const last = rows.length > 0 ? (rows[rows.length - 1] as ListRow).k : "";
    const done = page.next === "";
    let range: KeyRangeSpec | null = null;
    if (prev && (done || last !== "")) range = { prefix, lo: prev.lastKey, loOpen: true, hi: done ? "" : last, hiOpen: false };
    const complete = done && !!prev && prev.fromStart;
    await ctx.mirror.applyListPage(s, p, { prefix, rows, range, complete, at: t1, seenFrom: t0, seenTo: t1 });
    return true;
  }

  // fromMirror answers a list / getBatch page from the mirror. err is the
  // network failure being answered (null for an SDK cursor): with no on-device
  // answer — no partition, a stale one, or (for a first page) nothing mirrored
  // under the prefix and no complete listing covering it — err is rethrown.
  async function fromMirror<E>(kind: Kind, s: MirrorStoreName, prefix: string, opts: ListOptions | undefined, err: BacklitError | null): Promise<ListPage<E>> {
    const fail = err ?? new BacklitError("network", "no on-device copy for this cursor", 0);
    const p = await offlinePart(s);
    if (!p) throw fail;
    const n = opts?.limit;
    const limit =
      typeof n === "number" && Number.isFinite(n)
        ? Math.min(kind ? 500 : 1000, Math.max(1, Math.trunc(n)))
        : kind
          ? 100
          : 500;
    const { after, complete: whole } = opts?.cursor ? readCursor(opts.cursor) : { after: "", complete: true };
    let rows: MirrorEntry[];
    let covered: boolean;
    try {
      rows = await ctx.mirror.scan(s, p, prefix, { after, limit: limit + 1 });
      covered = await ctx.mirror.isCoveredAbsent(s, p, prefix);
      if (err && rows.length === 0 && !covered) throw err;
    } catch {
      throw fail;
    }
    const items: object[] = [];
    let more = rows.length > limit;
    let bytes = 0;
    let last = "";
    for (const e of rows.slice(0, limit)) {
      const v = e.value;
      if (kind && v) {
        if (items.length > 0 && bytes + v.byteLength > BATCH_BUDGET) {
          more = true;
          break;
        }
        bytes += v.byteLength;
      }
      items.push(row(kind, s, e.k, e.crc32c, e.contentType, e.size, e.owner, v));
      last = e.k;
    }
    // complete: a complete listing covers the prefix, every earlier page was
    // complete, and no unknown entry lies in this page's range — (after,
    // last], or (after, end] for the final page.
    const complete = whole && covered && !(await unknownIn(s, p, prefix, after, more ? last : ""));
    const next = more ? writeCursor(last, complete) : "";
    return { items, next, fromCache: true, complete } as unknown as ListPage<E>;
  }

  // unknownIn reports whether an unknown entry lies under prefix in (after,
  // hi] — (after, end] when hi is "". A store error answers true (so a page is
  // never claimed complete on a failed check).
  async function unknownIn(s: MirrorStoreName, p: string, prefix: string, after: string, hi: string): Promise<boolean> {
    const step = 256;
    try {
      for (let a = after; ; ) {
        const ms = await ctx.mirror.scanMeta(s, p, prefix, { after: a, limit: step });
        for (const m of ms) {
          if (hi !== "" && m.k > hi) return false;
          if (m.state === "unknown") return true;
        }
        if (ms.length < step) return false;
        a = (ms[ms.length - 1] as MirrorMeta).k;
      }
    } catch {
      return true;
    }
  }

  async function list<E extends ListEntry>(
    kind: Kind,
    s: MirrorStoreName,
    prefix: string,
    opts: ListOptions | undefined,
    call: () => Promise<ListPage<E>>,
  ): Promise<ListPage<E>> {
    return on(s) ? overlay(kind, s, prefix, opts, await page(kind, s, prefix, opts, call)) : call();
  }

  // overlay lays the current uid's queued ops over a page (online or from the
  // mirror): a queued put replaces its row (tagged pending), a queued delete
  // removes it, and a queued put of a key the page lacks is inserted when it
  // falls inside the page's key range. Skipped for a stale per-user partition.
  async function overlay<E>(kind: Kind, s: MirrorStoreName, prefix: string, opts: ListOptions | undefined, pg: ListPage<E>): Promise<ListPage<E>> {
    const uid = uidNow();
    const p = part(s);
    const mine = new Map<string, OutboxOp>();
    if (!uid || (p && stale(p))) return pg;
    try {
      const pre = hk(uid, s, prefix);
      if (![...(await pendingHint())].some((h) => h.startsWith(pre))) return pg;
      for (const o of await ctx.outbox.all()) if (o.uid === uid && o.store === s && o.key.startsWith(prefix)) mine.set(o.key, o);
    } catch {
      return pg;
    }
    if (mine.size === 0) return pg;
    const rows = pg.items as Row[];
    // The page's range: strictly after the key its cursor continues from (the
    // decoded SDK cursor, or the chained page's last key for a server cursor —
    // an unchained one falls back to the page's own first row, inclusive), up
    // to its last row unless it is the final page. An empty page has no first
    // or last row: k >= undefined / k <= undefined is false.
    const cursor = opts?.cursor ?? "";
    const from = cursor[0] === "!" ? readCursor(cursor).after : cursor ? chain.get(ck(s, prefix, cursor))?.lastKey : undefined;
    const first = rows[0]?.key as string;
    const hi = rows[rows.length - 1]?.key as string;
    const above = (k: string) => !cursor || (from !== undefined ? k > from : k >= first);
    const items: Row[] = [];
    const add = (o: OutboxOp, owner: string | undefined) =>
      o.bytes && items.push(row(kind, s, o.key, o.crc32c, o.contentType, o.bytes.byteLength, owner, o.bytes, { pending: true }) as Row);
    for (const it of rows) {
      const o = mine.get(it.key);
      if (!o) items.push(it);
      else if (mine.delete(it.key)) add(o, it.owner);
    }
    for (const o of mine.values()) {
      if (above(o.key) && (pg.next === "" || o.key <= hi)) add(o, uid);
    }
    items.sort((a, b) => (a.key < b.key ? -1 : 1));
    return { ...pg, items } as unknown as ListPage<E>;
  }

  async function page<E extends ListEntry>(
    kind: Kind,
    s: MirrorStoreName,
    prefix: string,
    opts: ListOptions | undefined,
    call: () => Promise<ListPage<E>>,
  ): Promise<ListPage<E>> {
    const cursor = opts?.cursor ?? "";
    if (cursor[0] === "!") return fromMirror<E>(kind, s, prefix, opts, null);
    try {
      const t0 = now();
      const ep = ctx.epoch();
      const page = await call();
      const t1 = now();
      ctx.noteOnline(true);
      const m = responseMetaOf(page);
      const folded = !!gate(s, m);
      const prev = link(s, prefix, cursor, page as ListPage<Row>, folded);
      if (folded) track(writePage(kind, s, prefix, page as ListPage<Row>, m, prev, t0, t1, ep));
      return page;
    } catch (e) {
      if (!isNet(e)) throw e;
      ctx.noteOnline(false);
      if (cursor) throw e; // a server cursor can't be continued from the mirror
      return fromMirror<E>(kind, s, prefix, opts, e);
    }
  }

  // Realtime frames (only from a socket the app opened) only ever invalidate
  // (→ "unknown"): a frame's commit happened at or before receipt, so a late
  // frame for an older commit must never state a value (a false CAS base). An
  // entry whose evidence began after receipt, or a set frame carrying the
  // stored crc, is left alone; with no entry, invalidate marks a covered key
  // unknown (and does nothing otherwise). The key's pending write-through
  // lands first, so this page's own echo (the same crc) never invalidates.
  const unobserve = observeStorageChanges((ev) => {
    const s = ev.store;
    const p = part(s);
    if (!on(s) || !p || stale(p)) return;
    const at = now();
    const ep = ctx.epoch();
    track(
      landed(s, p, ev.key).then(() => ctx.mirror.get(s, p, ev.key)).then((e) => {
        if (ctx.epoch() !== ep) return; // a purge since receipt
        if (e && seen(e)[0] > at) return;
        if (e && ev.operation === "set" && e.state === "present" && e.crc32c !== "" && e.crc32c === ev.crc32c) return;
        return ctx.mirror.invalidate(s, p, ev.key);
      }),
    );
  });

  // --- write path ---------------------------------------------------------------

  // view is k's local state for uid: the latest queued op decides (a put is
  // present, a delete absent), else the mirror — whose per-user partition is
  // unavailable while the identity is stale.
  async function view(uid: string, s: MirrorStoreName, k: string): Promise<View> {
    const last = (await ops(uid, s, k)).pop();
    if (last) return last.bytes ? { c: "present", crc32c: last.crc32c, bytes: last.bytes, contentType: last.contentType, owner: uid, pending: true } : { c: "absent" };
    const p = part(s);
    if (!p || stale(p)) return { c: "unavailable" };
    await landed(s, p, k);
    let e: MirrorEntry | null;
    try {
      e = await ctx.mirror.get(s, p, k, { touch: true });
      if (!e) return { c: (await ctx.mirror.isCoveredAbsent(s, p, k)) ? "absent" : "unseen" };
    } catch {
      return { c: "unavailable" };
    }
    if (e.state === "absent") return { c: "absent" };
    // An entry without a crc (an older server's listing, or an "unknown"
    // entry) is no base.
    return e.crc32c ? { c: "present", crc32c: e.crc32c, bytes: e.value, contentType: e.contentType, owner: e.owner } : { c: "presentUnknown" };
  }

  // mk is a new outbox op (queued, or quarantined when q) of k in store s for
  // uid: bytes null is a delete; its crc32c is the one the server will compute.
  // unseen marks a create-only put of a key the device had never seen.
  const mk = (
    uid: string,
    s: OutboxOp["store"],
    op: OpKind,
    k: string,
    bytes: ArrayBuffer | null,
    t: string,
    base: string | null,
    q: boolean,
    unseen = false,
  ): NewOutboxOp => {
    const at = now();
    return {
      ...(unseen ? { unseen: true as const } : {}),
      opId: randomHex(),
      uid,
      store: s,
      op,
      key: k,
      bytes,
      contentType: t,
      crc32c: bytes ? crcOf(bytes) : "",
      base,
      originTab: ctx.tabId,
      state: q ? "quarantined" : "queued",
      quarantinedAt: q ? at : 0,
      leaseUntil: 0,
      attempts: 0,
      lastError: "",
      createdAt: at,
      heldSince: 0,
      writes: 1,
    };
  };

  // queue adds n to the outbox (folded into the key's latest unsent op) and
  // tells this page (local, a data write's storage.change — capture has none),
  // the other tabs, the watchers and the drain. A full or unwritable outbox
  // throws fail. Resolves the op stored, or null when a fold cancelled it.
  async function queue(n: NewOutboxOp, fail: BacklitError, local?: StorageChangeEvent): Promise<OutboxOp | null> {
    const r = await ctx.outbox.enqueue(n).catch(() => undefined);
    if (r === "full") ctx.setReason("queue_full");
    if (r === "full" || r === undefined) throw fail;
    if (r) void hint?.then((h) => h.add(hk(n.uid, n.store, n.key)));
    else hint = null; // the fold cancelled the key's queued create
    if (local) emitLocalChange(n.uid, local);
    ctx.channel()?.post({ type: "outbox" });
    ctx.emit({ type: "queued", store: n.store, key: n.key, operation: opName(n) });
    void requestDrain();
    return r;
  }

  // queueCapture queues a capture create / update of handle k (never
  // quarantined: capture needs no identity) and answers it.
  async function queueCapture(op: "captureCreate" | "captureUpdate", k: string, bytes: ArrayBuffer, t: string, err: BacklitError | null) {
    const n = mk(CAPTURE_UID, "capture", op, k, bytes, t, null, false);
    await queue(n, err || full());
    return { crc32c: n.crc32c, pending: true as const };
  }

  // enqueue queues write w (0 put, 1 putIfMatch, 2 delete) of k for uid and
  // answers the call. err is the network failure being answered (null on the
  // no-overtaking path, which made no request).
  async function enqueue(
    s: MirrorStoreName,
    w: number,
    k: string,
    b: BodyInit | undefined,
    t: string | undefined,
    x: string | undefined,
    uid: string,
    err: BacklitError | null,
  ): Promise<unknown> {
    const fail = err || full();
    const bytes = w < 2 ? await read(b) : null;
    const v = await view(uid, s, k);
    const pv = v.c === "present" ? v : null;
    const gone = v.c === "absent";
    // unseen: putIfMatch(k, v, "") of a key the device has never seen queues
    // create-only, the check enforced at replay (a taken key → conflict).
    const unseen = w === 1 && v.c === "unseen" && x === "";
    if (w === 1 && !unseen) {
      // Local compare-and-swap.
      if (!pv && !gone) throw fail; // presentUnknown / unavailable / unseen with a crc (D-U1)
      if (pv ? pv.crc32c !== x : x !== "") {
        if (!pv) throw absent();
        if (!pv.bytes) throw fail; // metadata-only: no witness to show
        return { matched: false, witness: stored(s, pv.bytes, pv.contentType, pv.crc32c, pv.owner, carryTags(pv, { fromCache: true })) };
      }
    } else if (w > 1 && gone) {
      throw absent(); // a delete of a key known absent is never queued (CON-3)
    }
    // put: the known crc, "" when known absent or an unseen create
    // (create-only), null when unknown (a blind replay); a CAS match:
    // expected; delete: crc or null.
    const base = pv ? pv.crc32c : gone || unseen ? "" : null;
    // A stale identity still queues — quarantined.
    const q = isStale(ctx.identity() as IdentityRecord, now());
    const n = mk(uid, s, bytes ? "put" : "delete", k, bytes, bytes ? (t as string) : "", base, q, unseen);
    const crc = n.crc32c;
    const owner = own(s, pv?.owner || uid);
    const r = await queue(n, fail, { store: s, operation: bytes ? "set" : "delete", key: k, crc32c: crc, ...owner, pending: true });
    const out = { crc32c: crc, pending: true };
    if (w > 1) return;
    if (!w) return out;
    const op = r || n;
    return Object.defineProperty({ matched: true, ...out, ...owner }, OPID, { value: { opId: op.opId, slot: op.writes - 1 } });
  }

  async function write(
    s: MirrorStoreName,
    w: number,
    k: string,
    b: BodyInit | undefined,
    t: string | undefined,
    x: string | undefined,
    call: () => Promise<unknown>,
  ): Promise<unknown> {
    if (!on(s)) return call();
    const id = ctx.identity();
    const uid = id ? id.uid : "";
    // Who may queue: a signed-in user; shared data needs a non-viewer.
    const q = ctx.options().writes === "queue" && uid !== "" && (s !== "data" || (id as IdentityRecord).permission !== "viewer");
    if (q && (await ops(uid, s, k)).length > 0) return enqueue(s, w, k, b, t, x, uid, null);
    const ep = ctx.epoch();
    let r: unknown;
    let m: ResponseMeta | undefined;
    try {
      // A delete reads its response's meta (the public one resolves undefined).
      if (w === 2) m = await deleteWithMeta(s, k);
      else m = responseMetaOf((r = await call()));
    } catch (e) {
      if (!isNet(e)) {
        // A delete's or a CAS's 404 states the key absent.
        if (w > 0 && e instanceof BacklitError && e.code === "not_found" && e.status === 404) through(s, k, ep, responseMetaOf(e), 0);
        throw e;
      }
      ctx.noteOnline(false);
      if (!q) throw e;
      return enqueue(s, w, k, b, t, x, uid, e);
    }
    const c = r as { matched?: boolean; crc32c?: string; witness?: BlobResult } | undefined;
    const wit = c?.witness;
    through(s, k, ep, m, w === 2 ? 0 : c?.matched === false && wit ? { body: wit.value, crc: wit.crc32c, ct: wit.contentType } : { body: b, crc: c?.crc32c ?? "", ct: t as string });
    return r;
  }

  // through writes an acknowledged online write of k (requested at purge
  // epoch ep, answered with meta m) through to the mirror, on the serial
  // queue: a redirect or another user's uid touches nothing; no meta, or no
  // uid on a per-user partition, invalidates; else ack lands (see acked). The
  // purge epoch and the partition are re-checked right before each store call.
  function through(s: MirrorStoreName, k: string, ep: number, m: ResponseMeta | undefined, ack: Ack): void {
    const t1 = now();
    const p = part(s);
    if (!p || (m && (m.redirected || (m.uid !== "" && !writeThroughAllowed(p, m.uid))))) return;
    const live = () => ctx.epoch() === ep && part(s) === p;
    const job = async () => {
      const e = m && writeThroughAllowed(p, m.uid) ? await acked(s, p, k, m, t1, ack) : null;
      if (!live()) return;
      if (e && (await ctx.mirror.put(e)) !== "failed") return;
      if (live()) await ctx.mirror.invalidate(s, p, k);
    };
    const key = wk(s, p, k);
    const q = (wt = wt.then(job).catch(() => {}));
    inflight.set(key, q);
    track(
      q.then(() => {
        if (inflight.get(key) === q) inflight.delete(key);
      }),
    );
  }

  // acked is the entry an acknowledged write of k states, as evidence over
  // [m.sentAt, t1] — a tombstone, or the present value — or null (invalidate)
  // when it can't be trusted: a records answer without an owner, no crc, an
  // unreadable body, or an ArrayBuffer (view) whose bytes no longer match the
  // server's crc (mutated in flight). Past maxBytes / 4 only the metadata is
  // stored, and the body is never read.
  async function acked(s: MirrorStoreName, p: string, k: string, m: ResponseMeta, t1: number, ack: Ack): Promise<MirrorEntry | null> {
    if (!ack) return tomb(s, p, k, m.sentAt, t1);
    if ((s === "records" && m.owner === null) || !ack.crc) return null;
    let v: ArrayBuffer | null = null;
    let n = sizeOf(ack.body);
    if (n <= ctx.options().maxBytes / 4) {
      try {
        v = await read(ack.body);
      } catch {
        return null; // a detached buffer, a File's NotReadableError
      }
      if (mutable(ack.body) && crcOf(v) !== ack.crc) return null;
      n = v.byteLength;
    }
    const at = { fetchedAt: t1, accessedAt: 0, seenFrom: m.sentAt, seenTo: t1 };
    return { s, p, k, state: "present", value: v, contentType: ack.ct, crc32c: ack.crc, size: n, ...own(s, m.owner ?? ""), ...at };
  }

  // --- device-view reads (offline.peek / offline.cached) -------------------------

  // touched maps s\0p\0k to the last time a peek recorded an LRU access.
  const touched = new Map<string, number>();
  // touchDue reports (and records) whether a peek of key may touch now.
  const touchDue = (key: string): boolean => {
    const t = now();
    const last = touched.get(key);
    if (last !== undefined && t - last < TOUCH_EVERY_MS) return false;
    touched.delete(key);
    touched.set(key, t);
    if (touched.size > TOUCH_MAX) touched.delete(touched.keys().next().value as string);
    return true;
  };

  // devicePart is the partition a device-view read of s answers from, or null:
  // the store isn't mirrored, there is no partition, it was stale (deleted), or
  // it is a per-user one this page has neither proven nor seen a transport
  // failure for (R2-2).
  async function devicePart(s: MirrorStoreName): Promise<string | null> {
    if (!on(s)) return null;
    const p = await offlinePart(s);
    return p && (!isUserPartition(p) || ctx.hasPageProof() || ctx.transportFailed()) ? p : null;
  }

  // devicePeek is offline.peek: the uid's latest queued op, else the key's
  // present bytes on the device, else null.
  async function devicePeek(s: MirrorStoreName, k: string): Promise<BlobResult | RecordResult | null> {
    const p = await devicePart(s);
    if (!p) return null;
    const uid = uidNow();
    const last = (await ops(uid, s, k)).pop();
    if (last) {
      if (!last.bytes) return null; // a queued delete
      const e = s === "records" ? await peekEntry(s, p, k, false) : 0;
      return stored(s, last.bytes, last.contentType, last.crc32c, (e && e.owner) || uid, { pending: true }) as BlobResult;
    }
    await landed(s, p, k);
    try {
      const e = await ctx.mirror.get(s, p, k, { touch: touchDue(wk(s, p, k)) });
      return e && e.state === "present" && e.value ? (cachedResult(s, e) as BlobResult) : null;
    } catch {
      return null;
    }
  }

  // deviceCached is offline.cached: one metadata page of what the device holds
  // under prefix, the uid's queued ops overlaid. A store failure answers an
  // empty, incomplete page.
  async function deviceCached(s: MirrorStoreName, prefix: string, opts: ListOptions | undefined): Promise<DevicePage<DeviceEntry | RecordDeviceEntry>> {
    const empty = { items: [], next: "", fromCache: true as const, complete: false };
    const n = opts?.limit;
    const limit = typeof n === "number" && Number.isFinite(n) ? Math.min(1000, Math.max(1, Math.trunc(n))) : 500;
    const { after, complete: whole } = opts?.cursor ? readCursor(opts.cursor) : { after: "", complete: true };
    const p = await devicePart(s);
    if (!p) return empty;
    // The page's own pending write-throughs under prefix land first.
    const pre = wk(s, p, prefix);
    await Promise.all([...inflight].filter(([key]) => key.startsWith(pre)).map(([, q]) => q));
    // rows: up to limit + 1 known entries (the extra one says there is more);
    // unk: the unknown keys passed on the way (omitted from the page).
    const rows: MirrorMeta[] = [];
    const unk: string[] = [];
    let covered: boolean;
    try {
      covered = await ctx.mirror.isCoveredAbsent(s, p, prefix);
      for (let a = after; rows.length <= limit; ) {
        const ms = await ctx.mirror.scanMeta(s, p, prefix, { after: a, limit: limit + 1 });
        for (const m of ms) {
          if (rows.length > limit) break;
          if (m.state === "unknown") unk.push(m.k);
          else rows.push(m);
        }
        if (ms.length < limit + 1) break;
        a = (ms[ms.length - 1] as MirrorMeta).k;
      }
    } catch {
      return empty;
    }
    const more = rows.length > limit;
    const page = rows.slice(0, limit);
    const last = page.length > 0 ? (page[page.length - 1] as MirrorMeta).k : "";
    // complete: as a mirror page (fromMirror) — no unknown key in (after,
    // last], or (after, end] for the final page.
    const complete = whole && covered && !unk.some((k) => !more || k <= last);
    const items = page.map((m) => ({ key: m.k, crc32c: m.crc32c, contentType: m.contentType, size: m.size, hasValue: m.hasValue, ...own(s, m.owner) }));
    const pg = await overlay(0, s, prefix, opts, { items, next: more ? writeCursor(last, complete) : "", fromCache: true as const, complete });
    // An overlaid queued put carries its bytes.
    const out = (pg.items as Array<Row & { hasValue?: boolean }>).map((it) => ("hasValue" in it ? it : { ...it, hasValue: true }));
    return { ...pg, items: out, fromCache: true, complete } as DevicePage<DeviceEntry | RecordDeviceEntry>;
  }

  // Another tab's local change reaches this page's handlers only when it is
  // signed in as the same uid; an outbox poke drops the hint. The channel
  // opens after the decorator is built (offline.ts register), so wire it on
  // the next microtask.
  let offChannel = () => {};
  const wired = Promise.resolve().then(() => {
    const c = ctx.channel();
    if (c) {
      offChannel = c.on((m) => {
        if (m.type === "outbox") hint = null;
        else if (m.type === "change" && m.uid && m.uid === uidNow()) dispatchLocalChange(m.ev);
      });
    }
  });

  // createAtUnsupported: a create-at answered 404 / 405 (an older data-api),
  // so later online creates go straight to the legacy route — never sending a
  // body of up to 10 MiB twice.
  let createAtUnsupported = false;

  // The remote methods never use `this`, so a spread copy is safe. The capture
  // reads and recordsReassign stay pass-through.
  const backend: Backend = {
    ...remote,
    async authMe(): Promise<BacklitUser | null> {
      try {
        const u = await remote.authMe();
        ctx.noteOnline(true);
        if (u !== null) ctx.rememberUser(u);
        return u;
      } catch (err) {
        if (isNet(err)) {
          ctx.noteOnline(false);
          const u = ctx.cachedUser();
          if (u) return u;
        }
        throw err; // auth.me() still swallows it to null
      }
    },
    async captureCreate(prefix, body, t) {
      if (!captureOptIn()) return remote.captureCreate(prefix, body, t);
      const handle = prefix + "-" + uuidV4();
      validateHandle(handle);
      const bytes = await read(body);
      try {
        if (!createAtUnsupported) {
          try {
            return await captureCreateAt(handle, bytes, t);
          } catch (e) {
            const st = (e as BacklitError).status;
            if (st !== 404 && st !== 405) throw e;
            createAtUnsupported = true;
          }
        }
        return await remote.captureCreate(prefix, bytes, t);
      } catch (e) {
        if (!isNet(e)) throw e;
        ctx.noteOnline(false);
        // Queued under the handle minted here, even after a legacy fallback:
        // the replay is a create-at, so the handle the app holds is final.
        return { handle, ...(await queueCapture("captureCreate", handle, bytes, t, e)) };
      }
    },
    async captureUpdate(handle, body, t) {
      if (!captureOptIn()) return remote.captureUpdate(handle, body, t);
      const bytes = await read(body);
      // No overtaking: behind a queued op of this handle (its create) it queues.
      if ((await ops(CAPTURE_UID, "capture", handle)).length > 0) return queueCapture("captureUpdate", handle, bytes, t, null);
      try {
        return await remote.captureUpdate(handle, bytes, t);
      } catch (e) {
        if (!isNet(e)) throw e;
        ctx.noteOnline(false);
        return queueCapture("captureUpdate", handle, bytes, t, e);
      }
    },
  };
  // The overridden methods, per store: the reads <s>Get, <s>List and
  // <s>GetBatch, and the writes <s>Put, <s>PutIfMatch and <s>Delete (generated
  // by name — every one keeps the remote method's signature and result).
  type Read = (x: string, o?: ListOptions) => Promise<never>;
  type Write = (k: string, b?: BodyInit, t?: string, x?: string) => Promise<never>;
  const mine = backend as unknown as Record<string, Read & Write>;
  const theirs = remote as unknown as Record<string, Read & Write>;
  for (const s of MIRRORED) {
    const call = (m: string, x: string, o?: ListOptions) => (theirs[s + m] as Read)(x, o);
    mine[s + "Get"] = ((k: string) => get(s, k, () => call("Get", k))) as Read & Write;
    mine[s + "List"] = ((x: string, o?: ListOptions) => list(0, s, x, o, () => call("List", x, o))) as Read & Write;
    mine[s + "GetBatch"] = ((x: string, o?: ListOptions) => list(1, s, x, o, () => call("GetBatch", x, o))) as Read & Write;
    ["Put", "PutIfMatch", "Delete"].forEach((m, w) => {
      mine[s + m] = ((k: string, b?: BodyInit, t?: string, x?: string) =>
        counted(() => write(s, w, k, b, t, x, () => (theirs[s + m] as Write)(k, b, t, x)))) as Read & Write;
    });
  }
  // An opted-in capture create / update counts as an in-flight write too.
  for (const m of ["captureCreate", "captureUpdate"] as const) {
    const own = backend[m] as (a: string, b: BodyInit, t: string) => Promise<never>;
    backend[m] = ((a: string, b: BodyInit, t: string) => (captureOptIn() ? counted(() => own(a, b, t)) : own(a, b, t))) as never;
  }
  return {
    backend,
    dispose() {
      unobserve();
      void wired.then(() => offChannel());
    },
    async settle() {
      while (writes.size > 0) await Promise.all(writes);
    },
    inFlightWrites: () => calls,
    device: { peek: devicePeek, cached: deviceCached },
    fold: {
      page(kind, s, prefix, from, pg, m, iv) {
        const q = gate(s, m) && ctx.epoch() === iv.ep ? writePage(kind, s, prefix, pg, m, from, iv.t0, iv.t1, iv.ep) : Promise.resolve(false);
        track(q);
        return q;
      },
      value(s, k, r, m, iv) {
        const q = foldValue(s, k, r, m, iv);
        track(q);
        return q;
      },
    },
  };
}
